import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { TimeTravelContext } from 'components/TimeTravel/context';
import {Company, TagsTagTypeChoices} from 'libs/api/invest/generated/schema';
import { graphqlClient, useQuery } from 'utils/graphql';

import { SettingsType, MappedSettings } from '../dashboardSettingsTypes';
import { makeMappedSettings } from '../dashboardSettingsUtils';
import { SETTINGS_MAP } from './constants';


export function useCompany() {
  const { id: companyId } = useParams<{ id: string }>();
  const history = useHistory();
  const {date: asOfDate} = React.useContext(TimeTravelContext);

  const [company, setCompany] = React.useState<Company>();
  const [companyIsLoading, setCompanyIsLoading] = React.useState(true);
  const [mappedSettings, setMappedSettings] = React.useState<MappedSettings>();
  const [hasNoDefaultCompany, setHasNoDefaultCompany] = React.useState(false);

  const companies = useQuery(
    {
      companies: {
        id: 1,
        name: 1,
        allFundInvestments: {id: 1},
      }
    },
    'companies'
  )

  React.useEffect(() => {
    if (companyId) {
      setHasNoDefaultCompany(false);
      const queryParams = new URLSearchParams(window.location.search);
      loadCompany(
        companyId,
        (queryParams.get('aggrType') as TagsTagTypeChoices) || undefined,
        queryParams.get('aggrId') || null,
        queryParams.get('currency') || undefined,
      ).then();
    } else {
      setHasNoDefaultCompany(false);
      graphqlClient.chain.query
        .company()
        .execute({id: 1})
        .then(company => {
          if (!company) {
            setHasNoDefaultCompany(true);
            setCompanyIsLoading(false);
            return;
          }
          history.push(`/company/${company.id}`);
          loadCompany(company.id,undefined,null).then();
        })
    }
  }, [asOfDate]);

  React.useEffect(() => {
    if (!company) return;
    setMappedSettings(makeMappedSettings(
      company.dashboardSettings,
      SETTINGS_MAP,
    ));
  }, [company])

  async function loadCompany(
    companyId: string,
    aggregationType: TagsTagTypeChoices|undefined,
    aggregationId: string|null,
    currency?: string,
  ) {
    setCompanyIsLoading(true);
    await graphqlClient.chain.query
      .company({id: companyId, asOfDate, aggregationType, aggregationId, currency})
      .execute({
        __scalar: 1,
        clonedVersions: {
          clonedCapTableUrl: 1,
          clonedCapTableName: 1,
        },
        allFundInvestments: {
          id: 1,
          fund: {
            id: 1,
            name: 1,
            currency: 1,
            divisions: {__scalar: 1},
            groups: {__scalar: 1},
          },
        },
        dashboardSettings: {
          sections: {
            __scalar: 1,
            components: {__scalar: 1}
          }
        },
        defaultDashboardSettings: {
          sections: {
            __scalar: 1,
            components: {__scalar: 1}
          }
        },
        fundInvestments: {
          investmentMemos: {__scalar: 1},
          fund: {
            id: 1,
            name: 1,
            currency: 1,
          },
        },
        sector: {name: 1},
        topHolders: {__scalar: 1},
        ventureCapital: {siteMode: 1}
      })
      .then(response => setCompany(response || undefined))
      .finally(() => setCompanyIsLoading(false))
  }

  return {
    asOfDate,
    companies: companies.data,
    company,
    companyIsLoading,
    hasNoDefaultCompany,
    fundName: company?.aggregationId && !company?.aggregationType
      ? company?.allFundInvestments.find(fi => fi.fund.id === company?.aggregationId)?.fund.name
      : undefined,
    mappedSettings,

    onSettingsClose: () => loadCompany(companyId, undefined, null).then(),
    saveDashboardSettings: async (dashboardSettings: SettingsType) => {
      return await graphqlClient.chain.mutation
        .companyDashboardSettingsUpdate({companyId: companyId, dashboardSettings: dashboardSettings.sections})
        .execute({dashboardSettings: {dashboardType: 1}})
        .catch(err => console.error('Failed to save dashboard settings: ', err));
    },
    reloadCompany: loadCompany,
  }
}
